import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";

import { usePermissions } from "Flocknote/apiHooks";
import { useNetworkLogo } from "Flocknote/stateHooks";

import {
  useClearActiveAction,
  useNetworkImageActiveAction,
} from "Dashboard/components/WhatsNext/whatsNextStore";
import { useSetNetworkPhoto } from "NavBar/API/queries";
import { useNetworkImageRef } from "NavBar/navBarStore";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrDismissibleError from "UI/FnrDismissibleError";
import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import FnrOpacityToggle from "UI/FnrOpacityToggle";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrUploadButton from "UI/FnrUploadButton";
import { colorsByFunction } from "UI/colors";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";

const NetworkPhoto = () => {
  const [{ canUpdateNetworkImage }] = usePermissions();
  const [networkLogo = "", setNetworkLogo] = useNetworkLogo();
  const { mutateAsync: setNetworkPhoto, errorMessage } = useSetNetworkPhoto();
  const [showError, setShowError] = useState(false);
  const networkImageRef = useNetworkImageRef();
  const isHoverFocused = useIsHoverFocused(networkImageRef);
  const networkImageActiveAction = useNetworkImageActiveAction();
  const clearActiveAction = useClearActiveAction();

  useEffect(() => {
    if (errorMessage) setShowError(true);
  }, [errorMessage]);

  const handleChange = async (file) => {
    setShowError(false);
    const { url } = await setNetworkPhoto(file);
    setNetworkLogo(url);
  };

  const uploadButtonRef = useRef();

  const handleClick = useCallback(() => {
    clearActiveAction();
    uploadButtonRef.current.click();
  }, [clearActiveAction]);
  const title = useMemo(() => {
    if (networkLogo.includes("newimageeveryonegroupmainnetworkimage"))
      return "No file chosen";
    else return "Chose new file";
  }, [networkLogo]);

  return (
    <FnrTappyShrink muted={!canUpdateNetworkImage}>
      <SDiv>
        <SAbsoluteWrapper>
          <FnrUploadButton
            onChange={handleChange}
            ref={uploadButtonRef}
            hide
          />
        </SAbsoluteWrapper>
        <SFnrButtonWrapper
          onClick={canUpdateNetworkImage ? handleClick : undefined}
          ref={networkImageRef}
          data-cy="NavBar_NetworkPhoto_Button"
          $isClickable={canUpdateNetworkImage}
        >
          <SFnrNetworkPhoto
            title={title}
            size={null}
            width={60}
            src={networkLogo}
            isFlashing={networkImageActiveAction}
            flashToColor={colorsByFunction.defaultPhoto}
          />
        </SFnrButtonWrapper>
        <FnrDismissibleError
          show={showError}
          errorMessage={errorMessage}
          compact
        />
        <SFnrOpacityToggle
          show={Boolean(canUpdateNetworkImage && isHoverFocused)}
          scale
          data-cy="NavBar_NetworkPhoto_UploadArrow"
        >
          <SFnrContainedIcon
            icon="arrow-circle-up far"
            size="decorative3"
            color="textLight"
            noPadding
          />
        </SFnrOpacityToggle>
      </SDiv>
    </FnrTappyShrink>
  );
};

const SDiv = styled.div`
  position: relative;
  line-height: 0;
`;

const SAbsoluteWrapper = styled.div`
  position: absolute;
`;

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
`;

const SFnrNetworkPhoto = styled(FnrNetworkPhoto)`
  display: block;
`;

const SFnrOpacityToggle = styled(FnrOpacityToggle)`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  pointer-events: none;
  line-height: 1;
`;

const SFnrContainedIcon = styled(FnrContainedIcon)`
  text-shadow: #000 0 0 8px;
`;

export default NetworkPhoto;
